import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Plattform-Default (neutral InvictaFit), solange kein Studio erkannt wurde.
const DEFAULTS = {
  slug: null,
  name: 'InvictaFit',
  logoUrl: '/assets/invictafit-logo.png',
  studioId: null,
  isStudio: false,
  ready: false,
  // Branding des eingeloggten Mandanten (Firmenname + eigenes Logo)
  companyName: null,
  companyLogo: null,
};

const Ctx = createContext(DEFAULTS);
export const useTenantBranding = () => useContext(Ctx);

// Studio-URLs: /<studio>/admin, /<studio>/portal, /<studio>/login
const STUDIO_SUBPATHS = ['admin', 'portal', 'login'];

// App-eigene erste Pfadsegmente, die KEIN Mandanten-Slug sind, auch wenn das
// zweite Segment zufällig wie ein Studio-Subpath aussieht (z. B. das
// Einstellungs-Panel /settings/portal). Sonst würde "settings" als Slug
// erkannt, global als X-Tenant-Slug gesetzt und die Plattform-Login-Seite
// mit „Unbekannter Mandant" blockieren.
const RESERVED_FIRST_SEGMENTS = new Set([
  'settings', 'admin', 'dashboard', 'superadmin', 'communication',
  'portal-settings', 'whatsapp', 'kv', 'branchen',
]);

function detectSlug(pathname) {
  const seg = (pathname || '').split('/').filter(Boolean);
  if (seg.length >= 2 && STUDIO_SUBPATHS.includes(seg[1])) {
    const first = seg[0].toLowerCase();
    if (RESERVED_FIRST_SEGMENTS.has(first)) return null;
    return first;
  }
  return null;
}

export const TenantBrandingProvider = ({ children }) => {
  const location = useLocation();
  const [state, setState] = useState(DEFAULTS);

  useEffect(() => {
    const urlSlug = detectSlug(location.pathname);
    const slug = urlSlug || null;

    // Globalen X-Tenant-Slug AUSSCHLIESSLICH aus der URL ableiten – niemals aus
    // einem gespeicherten Slug. Ein alter/falscher Wert (z. B. "settings") würde
    // sonst auf Plattform-Seiten wie /login mitgesendet und dort 403
    // „Unbekannter Mandant" auslösen. Auf Nicht-Studio-Seiten Header entfernen.
    if (urlSlug) {
      localStorage.setItem('activeTenantSlug', urlSlug);
      axios.defaults.headers.common['X-Tenant-Slug'] = urlSlug;
    } else {
      delete axios.defaults.headers.common['X-Tenant-Slug'];
    }

    let cancelled = false;

    // Eingeloggter Mandant (Admin/Staff): eigenes Firmen-Branding laden
    // (Firmenname + hochgeladenes Logo), damit die Sidebar oben die eigene
    // Firma statt des Plattform-Defaults zeigt.
    const loadCompanyBranding = () => {
      if (!localStorage.getItem('token')) return;
      axios
        .get(`${API}/api/tenant-branding/me`)
        .then(({ data }) => {
          if (cancelled || !data) return;
          setState((prev) => ({
            ...prev,
            companyName: data.name || prev.companyName,
            companyLogo: data.logo_url || null,
          }));
        })
        .catch(() => {});
    };

    if (!slug) {
      setState({ ...DEFAULTS, ready: true });
      loadCompanyBranding();
      return () => { cancelled = true; };
    }

    axios
      .get(`${API}/api/public/tenant-branding/${slug}`)
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.exists) {
          setState((prev) => ({
            ...prev,
            slug: data.slug,
            name: data.name,
            logoUrl: data.logo_url || DEFAULTS.logoUrl,
            studioId: data.studio_id,
            isStudio: true,
            ready: true,
          }));
        } else {
          setState((prev) => ({ ...DEFAULTS, companyName: prev.companyName, companyLogo: prev.companyLogo, ready: true }));
        }
      })
      .catch(() => {
        if (!cancelled) setState((prev) => ({ ...DEFAULTS, companyName: prev.companyName, companyLogo: prev.companyLogo, ready: true }));
      });

    loadCompanyBranding();

    return () => { cancelled = true; };
  }, [location.pathname]);

  return <Ctx.Provider value={state}>{children}</Ctx.Provider>;
};
